/* eslint-disable indent */
import { html, css, nothing, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import { loadAccreditationsIfNeeded, exportAccreditations, retryAccreditation } from '../../store/accreditation/accreditation.js';
import { loadOrganizationOptionsIfNeeded } from '../../store/accreditationInfo/accreditationInfo.js';
import sharedStyles from '../../app/styles.js';
import { translate } from '../../helpers/translate.js';
import { formatDate } from '../../helpers/controls.js';
import '@lit-labs/virtualizer';
import '@vaadin/form-layout/theme/material/vaadin-form-layout.js';
import '@vaadin/select/theme/material/vaadin-select.js';
import '@vaadin/text-field/theme/material/vaadin-text-field.js';
import '@sdb-develop/learning-frontend/dist/src/sdb-content-date-picker';
import '../shared/sdb-filter-button.js';
import '../shared/sdb-loading.js';
import '../shared/sdb-search-input.js';
import '../shared/sdb-side-sheet.js';
import '../shared/sdb-status-message.js';
import '../shared/sdb-dialog.js';

// Result statuses as the Learning API returns them (PeOnlineResultStatus, camelCase).
const STATUSES = ['accepted', 'failed', 'pending', 'notValid', 'badResponse', 'error'];
// Sort orders of the Learning API (PeOnlineAccreditationSortOrder).
const SORT_ORDERS = ['ProcessedNewest', 'ProcessedOldest', 'User', 'Status'];
const DEFAULT_SORT = 'ProcessedNewest';
// vaadin-select turns an item without a value into its label, so "all" gets a sentinel.
const ALL = 'all';

// Filters in the side sheet; the search texts stay in the header.
const SHEET_FILTER_DEFAULTS = {
    from: null, to: null, status: '', organization: '', externalPersonId: '', peCourseId: '', sort: DEFAULT_SORT,
};

// Reasons the Learning API gives for not sending an accreditation again (409/422 `reason`).
const RETRY_REASONS = ['alreadyAccepted', 'noUserRegistration', 'noApplicableMapping'];

// The Learning API has no result yet for an accreditation that still has to be sent to PE-online.
const getResultStatus = (accreditation) => accreditation.result?.resultStatus ?? 'pending';

// Anything PE-online did not accept can be sent again; that needs the id of the stored accreditation.
const canRetry = (accreditation) => Boolean(accreditation.id) && getResultStatus(accreditation) !== 'accepted';

// The date picker reports local midnight as a UTC ISO string; the API expects the local date.
const toLocalDate = (iso) => {
    if (!iso) {
        return null;
    }

    const date = new Date(iso);
    return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0'),
    ].join('-');
};

@customElement('sdb-accreditation-list')
class SdbAccreditationList extends connect(store)(LitElement) {

    @state()
    accessor accreditations = [];

    @state()
    accessor total;

    @state()
    accessor isLoadingFirstTime;

    @state()
    accessor filters = {};

    @state()
    accessor organizationOptions = [];

    @state()
    accessor filtersOpened = false;

    @state()
    accessor exportStatus;

    @state()
    accessor exportError;

    @state()
    accessor retries = {};

    stateChanged(state) {
        this.accreditations = state.accreditation?.allList;
        this.total = state.accreditation?.allTotal;
        this.isLoadingFirstTime = state.accreditation?.listStatus === 'loading' && state.accreditation?.listPage === -1;
        this.filters = state.accreditation?.filters ?? {};
        this.organizationOptions = state.accreditationInfo?.organizationOptions ?? [];
        this.exportStatus = state.accreditation?.exportStatus;
        this.exportError = state.accreditation?.exportError;
        this.retries = state.accreditation?.retries ?? {};
    }

    get activeFilterCount() {
        return Object.entries(SHEET_FILTER_DEFAULTS)
            .filter(([key, defaultValue]) => (this.filters[key] || null) !== (defaultValue || null))
            .length;
    }

    static get styles() {
        return [
            sharedStyles,
            css`
                :host {
                    max-width: var(--tcg-content-max-width);
                    width: 100%;
                }

                #accreditations_list {
                    contain: none !important; /* lit-virtualizer hack */
                }

                vaadin-text-field[readonly] {
                    --_material-text-field-input-line-opacity: 0;
                }

                .toolbar {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .toolbar sdb-search-input {
                    flex: 1;
                    min-width: 0;
                }

                .retry {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 8px;
                    margin-top: 16px;
                }

                .retry p {
                    margin: 0;
                    font-size: 14px;
                    text-align: right;
                }

                .retry p.refused {
                    color: var(--app-error-color, firebrick);
                }
            `,
        ];
    }

    render() {
        if (this.isLoadingFirstTime) {
            return html`<sdb-loading></sdb-loading>`;
        }

        return html`
            <sdb-layout-50-50>
                <sdb-nav-titlebar slot="left">
                    <span>${translate('menuAccreditations')}</span>
                    <span ?hidden=${this.total === undefined}>&nbsp;(${this.total})</span>
                </sdb-nav-titlebar>
                <div slot="right" class="toolbar">
                    <sdb-search-input
                        placeholder=${translate('searchCourse')}
                        value=${this.filters.course ?? ''}
                        @search=${(e) => { this._filter({ course: e.detail.searchText }); }}>
                    </sdb-search-input>
                    <sdb-search-input
                        placeholder=${translate('searchUser')}
                        value=${this.filters.user ?? ''}
                        @search=${(e) => { this._filter({ user: e.detail.searchText }); }}>
                    </sdb-search-input>
                    <sdb-filter-button
                        label=${translate('filters')}
                        count=${this.activeFilterCount}
                        @click=${() => { this._openFilters(); }}>
                    </sdb-filter-button>
                    <sdb-content-iconbutton
                        icon="fal fa-file-excel"
                        title=${translate('exportExcel')}
                        aria-label=${translate('exportExcel')}
                        ?disabled=${!this.total || this.exportStatus === 'loading'}
                        @click=${() => { store.dispatch(exportAccreditations()); }}>
                    </sdb-content-iconbutton>
                </div>
            </sdb-layout-50-50>

            <sdb-side-sheet
                heading=${translate('filters')}
                ?opened=${this.filtersOpened}
                @close=${() => { this.filtersOpened = false; }}>
                <vaadin-select
                    label=${translate('sortBy')}
                    value=${this.filters.sort || DEFAULT_SORT}
                    .items=${SORT_ORDERS.map((sort) => ({ value: sort, label: translate(`sortAccreditations.${sort}`) }))}
                    @change=${(e) => { this._filter({ sort: e.target.value }); }}>
                </vaadin-select>
                <sdb-content-date-picker
                    label=${translate('processedFrom')}
                    max=${this.filters.to ?? nothing}
                    value=${this.filters.from ?? nothing}
                    @change=${(e) => { this._filter({ from: toLocalDate(e.detail.value) }); }}>
                </sdb-content-date-picker>
                <sdb-content-date-picker
                    label=${translate('processedTo')}
                    min=${this.filters.from ?? nothing}
                    value=${this.filters.to ?? nothing}
                    @change=${(e) => { this._filter({ to: toLocalDate(e.detail.value) }); }}>
                </sdb-content-date-picker>
                <vaadin-select
                    label=${translate('status')}
                    value=${this.filters.status || ALL}
                    .items=${[
                        { value: ALL, label: translate('allStatuses') },
                        ...STATUSES.map((status) => ({ value: status, label: translate(`accr.status.${status}`) })),
                    ]}
                    @change=${(e) => { this._filter({ status: e.target.value === ALL ? '' : e.target.value }); }}>
                </vaadin-select>
                <vaadin-select
                    label=${translate('pe.organization')}
                    value=${this.filters.organization || ALL}
                    .items=${[{ value: ALL, label: translate('allOrganizations') }, ...this.organizationOptions]}
                    @change=${(e) => { this._filter({ organization: e.target.value === ALL ? '' : e.target.value }); }}>
                </vaadin-select>
                <vaadin-text-field
                    label=${translate('pe.externalPersonId')}
                    clear-button-visible
                    .value=${this.filters.externalPersonId ?? ''}
                    @change=${(e) => { this._filter({ externalPersonId: e.target.value.trim() }); }}>
                </vaadin-text-field>
                <vaadin-text-field
                    label=${translate('pe.courseId')}
                    clear-button-visible
                    .value=${this.filters.peCourseId ?? ''}
                    @change=${(e) => { this._filter({ peCourseId: e.target.value.trim() }); }}>
                </vaadin-text-field>

                <sdb-content-button
                    slot="actions"
                    label=${translate('resetFilters')}
                    ?disabled=${this.activeFilterCount === 0}
                    @click=${() => { this._filter({ ...SHEET_FILTER_DEFAULTS }); }}>
                </sdb-content-button>
            </sdb-side-sheet>

            <sdb-layout-100>
                ${!this.total ? html`
                    <sdb-status-message label=${translate('noAccreditationsFound')}>
                    </sdb-status-message>
                ` : html`
                    <sdb-content-accordion separator>

                        <sdb-content-accordionheading class="hideonmobile">
                            <sdb-content-headingitem
                                textDark
                                slot="1"
                                icon="fal fa-users"
                                headline=${translate('course')}>
                            </sdb-content-headingitem>
                            <sdb-content-headingitem
                                textDark
                                slot="2"
                                icon="fal fa-user"
                                headline=${translate('user')}>
                            </sdb-content-headingitem>
                            <sdb-content-headingitem
                                textDark
                                slot="3"
                                icon="fal fa-calendar"
                                headline=${translate('processed')}>
                            </sdb-content-headingitem>
                            <sdb-content-headingitem
                                textDark
                                slot="4"
                                icon="fal fa-check"
                                headline=${translate('Status')}>
                            </sdb-content-headingitem>
                        </sdb-content-accordionheading>

                        <lit-virtualizer
                            id="accreditations_list"
                            .items=${this.accreditations}
                            totalItems=${this.total}
                            .renderItem=${(accreditation, index) => this._renderAccreditationItem(accreditation, index)}
                            .scrollTarget=${document}>
                        </lit-virtualizer>

                    </sdb-content-accordion>
                `}
            </sdb-layout-100>

            <sdb-dialog
                type="info"
                heading=${translate('exportFailed')}
                body=${this.exportError ?? ''}
                .opened=${this.exportStatus === 'failed'}>
            </sdb-dialog>

            <sdb-dialog
                type="confirm"
                id="retry_dialog"
                heading=${translate('accrRetry.dialogTitle')}
                body=${`${translate('accrRetry.dialogBody')}<br><br><em>${translate('accrRetry.dialogDisclaimer')}</em>`}
                confirmCaption=${translate('accrRetry.confirm')}
                @confirm=${(e) => { store.dispatch(retryAccreditation(e.detail.args)); }}>
            </sdb-dialog>
        `;
    }

    _renderAccreditationItem(accreditation, index) {
        store.dispatch(loadAccreditationsIfNeeded({ index }));

        const status = getResultStatus(accreditation);

        return html`
            <sdb-content-accordionitem
                headline=${accreditation.courseName}
                overline=${accreditation.accreditationKey ?? nothing}>
                <div slot="2">${accreditation.displayName}</div>
                <div slot="3">${formatDate(accreditation.created)}</div>
                <div slot="4">${this._renderStatusChip(status, translate('accr.status.' + status))}</div>

                <vaadin-form-layout>
                    ${this._renderField(translate('status'), translate('accr.status.' + status))}
                    <div></div>

                    ${this._renderField(translate('pe.organization'), accreditation.accreditationKey)}
                    ${this._renderField(translate('processed'), formatDate(accreditation.created))}

                    ${accreditation.result?.message ? this._renderField(translate('result'), accreditation.result.message, 2) : nothing}

                    ${this._renderField(translate('pe.externalPersonId'), accreditation.data.externalPersonId)}
                    ${this._renderField(translate('points'), accreditation.points)}

                    ${this._renderField(translate('pe.courseId'), accreditation.data.course?.peCourseId)}
                    ${this._renderField(translate('accr.endDate'), formatDate(accreditation.data.endDate))}

                    ${accreditation.data.course?.peModuleId
                        ? this._renderField(translate('pe.moduleId'), accreditation.data.course.peModuleId)
                        : nothing
                    }
                    ${accreditation.data.course?.peEditionId
                        ? this._renderField(translate('pe.editionId'), accreditation.data.course.peEditionId)
                        : nothing
                    }
                </vaadin-form-layout>

                ${this._renderRetry(accreditation)}
            </sdb-content-accordionitem>
        `;
    }

    _renderRetry(accreditation) {
        const retry = this.retries[accreditation.id];

        if (!canRetry(accreditation) && !retry) {
            return nothing;
        }

        return html`
            <div class="retry">
                ${canRetry(accreditation) ? html`
                    <sdb-content-button
                        label=${translate('accrRetry.button')}
                        outlined
                        ?disabled=${retry?.status === 'sending'}
                        @click=${() => { this._openRetryDialog(accreditation); }}>
                    </sdb-content-button>
                ` : nothing}
                ${this._renderRetryMessage(accreditation, retry)}
            </div>
        `;
    }

    _renderRetryMessage(accreditation, retry) {
        switch (retry?.status) {
        case 'sending':
            return html`<p>${translate('accrRetry.sending')}</p>`;
        case 'sent':
            return html`<p>${translate('accrRetry.sent', { status: translate('accr.status.' + getResultStatus(accreditation)) })}</p>`;
        case 'refused':
            return html`<p class="refused">${translate('accrRetry.notSent', {
                reason: translate(`accrRetry.${RETRY_REASONS.includes(retry.reason) ? retry.reason : 'failed'}`),
            })}</p>`;
        default:
            return nothing;
        }
    }

    _openRetryDialog(accreditation) {
        this.renderRoot.querySelector('#retry_dialog').open({ id: accreditation.id, targetId: accreditation.targetId });
    }

    _filter(changes) {
        store.dispatch(loadAccreditationsIfNeeded({ filters: changes, reload: true }));
    }

    _openFilters() {
        store.dispatch(loadOrganizationOptionsIfNeeded());
        this.filtersOpened = true;
    }

    _renderField(label, value, colspan = 1) {
        return html`
            <vaadin-text-field
                readonly
                colspan=${colspan}
                label=${label}
                value=${value}>
            </vaadin-text-field>
        `;
    }

    _renderStatusChip(status, label) {
        return html`
            <sdb-content-chipbar>
                <sdb-content-chip
                    light
                    label=${label}
                    color=${this._getStatusColor(status)}>
                </sdb-content-chip>
            </sdb-content-chipbar>
        `;
    }

    _getStatusColor = (status) => {
        switch (status) {
            case 'accepted':
                return 'green';
            case 'badResponse':
            case 'error':
            case 'notValid':
            case 'failed':
                return 'firebrick';
            case 'pending':
                return 'grey';
        }
    };
}
